import { useMemo } from 'react'
import { DateRange } from 'react-day-picker'

import { FiltersDefinition, FiltersState, IconType } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { AnalyticsTraceabilityResultsDocument } from 'generated/resources/graphql'
import moment from 'moment'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { filterDefinitionsFromSchema } from 'modules/ai_reports/components/DatasourceTypeRenderer/filters'
import { ColumnFormatHint } from 'modules/ai_reports/components/DatasourceTypeRenderer/utils'

import TraceabilityTableContent from './TraceabilityTableContent'

interface ReportTableAction {
  label: string
  icon: IconType
  onClick: () => void
}

interface ReportTableProps {
  id: string
  dateFilter?: DateRange
  reportName: string
  sqlQuery: string
  actions?: {
    primary?: ReportTableAction
    secondary?: ReportTableAction[]
  }
  columnFormatHints: ColumnFormatHint[]
  onStateChange: React.ComponentProps<typeof OneDataCollection>['onStateChange']
  tableId: string
  onUserFacingSqlQueryChange: (sqlQuery: string) => void
  currentFilters: FiltersState<FiltersDefinition<string>> | undefined
}

const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 10

const ReportTable = ({
  id,
  sqlQuery,
  actions,
  columnFormatHints,
  onStateChange,
  tableId,
  dateFilter,
  onUserFacingSqlQueryChange,
  currentFilters,
}: ReportTableProps) => {
  const { company } = useCurrentSession()

  // Used to fetch the columns.
  const { data: columnsData } = useQuery(AnalyticsTraceabilityResultsDocument, {
    variables: {
      query: sqlQuery,
      companyId: company.id,
      dateRange: {
        from: (dateFilter?.from
          ? new Date(dateFilter.from)
          : moment().endOf('day').toDate()
        ).toISOString(),
        to: (dateFilter?.to
          ? new Date(dateFilter.to)
          : moment().endOf('day').toDate()
        ).toISOString(),
      },
      page: INITIAL_PAGE,
      perPage: INITIAL_PAGE_SIZE,
      tableId: tableId,
    },
  })

  // FIXME: The Data Collection filters are not updated unless they are added to
  // the Data Collection source dependencies array. If this is fixed, we can
  // remove the hook and use the filters directly.
  const schema = columnsData?.iris?.traceabilityResultsConnection?.nodes?.[0]?.schema

  const filterDefinitions = useMemo(() => filterDefinitionsFromSchema(schema ?? []), [schema])

  if (!columnsData) {
    return <Loading />
  }

  return (
    <TraceabilityTableContent
      id={id}
      sqlQuery={sqlQuery}
      tableId={tableId}
      actions={actions}
      columnFormatHints={columnFormatHints}
      onStateChange={onStateChange}
      dateFilter={dateFilter}
      onUserFacingSqlQueryChange={onUserFacingSqlQueryChange}
      columnsData={columnsData}
      filterDefinitions={filterDefinitions}
      currentFilters={currentFilters}
    />
  )
}

export default ReportTable
export { type ReportTableAction, type ReportTableProps }
